/*
 * Shareable cart links.
 *
 * Link format: /cart?shared=VARIANT_ID:QTY,VARIANT_ID:QTY
 *
 * Sender: a [data-cart-share] button on the cart page builds the link from /cart.js and
 * shares it (touch devices) or copies it to the clipboard.
 *
 * Recipient: on /cart?shared=… each line is topped up to the shared quantity (only the
 * difference is added), one line at a time so an archived or deleted variant is skipped
 * instead of failing the rest. Then the page is replaced with plain /cart so a reload or
 * the back button cannot apply the link again. The recipient's own items are kept.
 *
 * Shopify's native /cart/VID:QTY permalink was rejected: one unavailable variant empties
 * the whole cart, and it replaces whatever the recipient already had.
 */
(function () {
  var NOTICE_KEY = 'cartShareSkipped';
  var MAX_LINES = 100;

  function parseShared(value) {
    var lines = [];
    String(value || '')
      .split(',')
      .forEach(function (part) {
        var m = part.trim().match(/^(\d{1,20}):(\d{1,4})$/);
        if (!m) return;
        var qty = parseInt(m[2], 10);
        if (qty > 0) lines.push({ id: Number(m[1]), quantity: qty });
      });
    return lines.slice(0, MAX_LINES);
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('cart.js ' + r.status);
      return r.json();
    });
  }

  function applySharedCart(lines) {
    return getCart().then(function (cart) {
      var have = {};
      cart.items.forEach(function (item) {
        have[item.variant_id] = (have[item.variant_id] || 0) + item.quantity;
      });
      var skipped = 0;
      // Sequential on purpose: parallel writes to one cart race each other.
      return lines
        .reduce(function (chain, line) {
          return chain.then(function () {
            var missing = line.quantity - (have[line.id] || 0);
            if (missing <= 0) return;
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: line.id, quantity: missing }] }),
            })
              .then(function (r) {
                if (!r.ok) skipped++;
              })
              .catch(function () {
                skipped++;
              });
          });
        }, Promise.resolve())
        .then(function () {
          return skipped;
        });
    });
  }

  function handleIncomingLink() {
    if (window.location.pathname.replace(/\/$/, '') !== '/cart') return;
    var params = new URLSearchParams(window.location.search);
    if (!params.has('shared')) return;
    var lines = parseShared(params.get('shared'));
    var done = function (skipped) {
      try {
        if (skipped) sessionStorage.setItem(NOTICE_KEY, String(skipped));
      } catch (e) {}
      window.location.replace('/cart');
    };
    if (!lines.length) return done(0);
    applySharedCart(lines).then(done, function () {
      done(lines.length);
    });
  }

  function showSkippedNotice() {
    var skipped;
    try {
      skipped = sessionStorage.getItem(NOTICE_KEY);
      sessionStorage.removeItem(NOTICE_KEY);
    } catch (e) {}
    if (!skipped) return;
    var container = document.querySelector('cart-items .page-width') || document.getElementById('MainContent');
    if (!container) return;
    var note = document.createElement('p');
    note.className = 'cart-share__notice';
    note.setAttribute('role', 'status');
    note.textContent =
      skipped === '1'
        ? '1 item from the shared cart is no longer available and was not added.'
        : skipped + ' items from the shared cart are no longer available and were not added.';
    container.insertBefore(note, container.firstChild);
  }

  function buildLink(cart) {
    var totals = {};
    var order = [];
    cart.items.forEach(function (item) {
      if (!(item.variant_id in totals)) {
        totals[item.variant_id] = 0;
        order.push(item.variant_id);
      }
      totals[item.variant_id] += item.quantity;
    });
    var shared = order
      .map(function (id) {
        return id + ':' + totals[id];
      })
      .join(',');
    return window.location.origin + '/cart?shared=' + shared;
  }

  function setStatus(button, text) {
    var label = button.querySelector('.cart-share__label');
    if (!label) return;
    if (!button.dataset.label) button.dataset.label = label.textContent;
    label.textContent = text;
    clearTimeout(button._cartShareTimer);
    button._cartShareTimer = setTimeout(function () {
      label.textContent = button.dataset.label;
    }, 2500);
  }

  function showFallback(button, url) {
    var field = button.parentNode.querySelector('.cart-share__fallback');
    if (!field) {
      field = document.createElement('input');
      field.type = 'text';
      field.readOnly = true;
      field.className = 'cart-share__fallback field__input';
      field.setAttribute('aria-label', 'Shareable cart link');
      button.parentNode.insertBefore(field, button.nextSibling);
    }
    field.value = url;
    field.focus();
    field.select();
    setStatus(button, 'Copy the link below');
  }

  function copy(button, url) {
    if (navigator.clipboard && window.isSecureContext) {
      // writeText can hang (pending permission, unfocused window); never leave the
      // shopper with no feedback.
      var timeout = new Promise(function (resolve) {
        setTimeout(function () {
          resolve('timeout');
        }, 1500);
      });
      return Promise.race([navigator.clipboard.writeText(url), timeout]).then(
        function (result) {
          if (result === 'timeout') return showFallback(button, url);
          setStatus(button, 'Link copied');
        },
        function () {
          showFallback(button, url);
        }
      );
    }
    showFallback(button, url);
    return Promise.resolve();
  }

  function onShareClick(button) {
    getCart()
      .then(function (cart) {
        if (!cart.items.length) return;
        var url = buildLink(cart);
        var touch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
        if (touch && navigator.share) {
          return navigator.share({ title: 'My cart at Vacuums & More', url: url }).catch(function (e) {
            if (e && e.name === 'AbortError') return;
            return copy(button, url);
          });
        }
        return copy(button, url);
      })
      .catch(function () {
        setStatus(button, 'Could not build link, try again');
      });
  }

  // Delegated: cart.js re-renders the cart footer (and this button) on every change.
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-cart-share]');
    if (!button) return;
    event.preventDefault();
    onShareClick(button);
  });

  handleIncomingLink();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', showSkippedNotice);
  } else {
    showSkippedNotice();
  }
})();
